/**
 * Skill Editor UI (V2)
 * Create, customize, and manage skills using the V2 delivery-type system.
 * Two render modes: standalone popup and embedded tab.
 */

import { Renderer } from '@client/core/Renderer';
import { InputManager } from '@client/input/InputManager';
import { Player } from '@client/entities/Player';
import { CustomSkillV2, SkillCreatorV2 } from '@shared/skills/SkillCreatorV2';
import {
  SkillDeliveryType,
  DELIVERY_TYPE_INFO,
  DELIVERY_TYPE_SLIDERS,
  DELIVERY_TYPES,
  ELEMENTS,
  ELEMENT_INFO,
  SKILL_CHOICES,
} from '@shared/skills/SkillDeliveryTypes';
import { ElementType } from '@shared/types';

// ========================
// Constants
// ========================

const BG = '#1a1815';
const BG_DARK = '#0d0a08';
const BORDER = '#3a3530';
const TEXT = '#f4e8d4';
const TEXT_DIM = '#8a7e6e';
const GOLD = '#ffd700';
const LIST_BG = '#14110e';
const SLIDER_TRACK = '#2a2520';
const HOVER_BG = '#2a2520';
const BTN_BG = '#252018';

type EditorTab = 'settings' | 'stats';
type CreationStep = 'type' | 'element';

interface Callbacks {
  onSkillChange?: () => void;
}

// ========================
// SkillEditorUI Class
// ========================

export class SkillEditorUI {
  private player: Player;
  private screenWidth: number;
  private screenHeight: number;

  // Panel geometry (set in render/renderInBounds)
  private panelX = 0;
  private panelY = 0;
  private panelWidth = 0;
  private panelHeight = 0;
  private scale = 1;

  // Visibility
  private isOpen = false;
  private fadeIn = 0;

  // Selection
  private selectedIndex = 0;
  private activeTab: EditorTab = 'settings';

  // New-skill creation flow
  private isCreating = false;
  private creationStep: CreationStep = 'type';
  private pendingDeliveryType: SkillDeliveryType | null = null;

  // Interaction state
  private draggingSlider: string | null = null;
  private listScrollY = 0;
  private mouseX = 0;
  private mouseY = 0;
  private callbacks: Callbacks = {};

  // Layout regions (recalculated per frame)
  private listRect = { x: 0, y: 0, w: 0, h: 0 };
  private detailRect = { x: 0, y: 0, w: 0, h: 0 };
  private hotbarRect = { x: 0, y: 0, w: 0, h: 0 };

  constructor(player: Player, screenW: number, screenH: number) {
    this.player = player;
    this.screenWidth = screenW;
    this.screenHeight = screenH;
  }

  // ========================
  // Public API
  // ========================

  open(): void { this.isOpen = true; this.fadeIn = 0; this.isCreating = false; }
  close(): void { this.isOpen = false; this.draggingSlider = null; }
  toggle(): void { this.isOpen ? this.close() : this.open(); }
  isVisible(): boolean { return this.isOpen; }
  resize(w: number, h: number): void { this.screenWidth = w; this.screenHeight = h; }
  setCallbacks(cb: Callbacks): void { this.callbacks = cb; }

  update(deltaTime: number): void {
    if (!this.isOpen) return;
    if (this.fadeIn < 1) this.fadeIn = Math.min(1, this.fadeIn + deltaTime * 5);
  }

  // ========================
  // Render: Standalone Popup
  // ========================

  render(renderer: Renderer): void {
    if (!this.isOpen) return;
    const ctx = renderer.context;
    const alpha = this.fadeIn;

    // Dim overlay
    ctx.fillStyle = `rgba(0,0,0,${0.7 * alpha})`;
    ctx.fillRect(0, 0, this.screenWidth, this.screenHeight);

    // Center panel (90% of screen, capped at 1000x700)
    const pw = Math.min(this.screenWidth * 0.9, 1000);
    const ph = Math.min(this.screenHeight * 0.85, 700);
    const px = (this.screenWidth - pw) / 2;
    const py = (this.screenHeight - ph) / 2;

    ctx.save();
    ctx.globalAlpha = alpha;

    // Panel bg
    const grad = ctx.createLinearGradient(px, py, px, py + ph);
    grad.addColorStop(0, '#1a1510');
    grad.addColorStop(1, BG_DARK);
    ctx.fillStyle = grad;
    roundRect(ctx, px, py, pw, ph, 10);
    ctx.fill();

    // Border
    ctx.strokeStyle = '#4a3f35';
    ctx.lineWidth = 2;
    roundRect(ctx, px, py, pw, ph, 10);
    ctx.stroke();

    this.panelX = px;
    this.panelY = py;
    this.panelWidth = pw;
    this.panelHeight = ph;
    this.scale = Math.max(0.6, Math.min(1.0, pw / 900));

    this.renderContent(ctx);
    ctx.restore();
  }

  // ========================
  // Render: Inside Bounds (Tab Mode)
  // ========================

  renderInBounds(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void {
    this.panelX = x;
    this.panelY = y;
    this.panelWidth = w;
    this.panelHeight = h;
    this.isOpen = true;
    this.fadeIn = 1;
    this.scale = Math.max(0.6, Math.min(1.0, w / 900));

    ctx.save();
    ctx.fillStyle = BG_DARK;
    roundRect(ctx, x, y, w, h, 5);
    ctx.fill();

    this.renderContent(ctx);
    ctx.restore();
  }

  // ========================
  // Shared Content Render
  // ========================

  private renderContent(ctx: CanvasRenderingContext2D): void {
    if (this.isCreating) {
      this.renderCreationFlow(ctx);
      return;
    }
    this.computeLayout();
    this.renderSkillList(ctx);
    this.renderDetailPanel(ctx);
    this.renderHotbar(ctx);
  }

  // ========================
  // Layout Computation
  // ========================

  private computeLayout(): void {
    const s = this.scale;
    const px = this.panelX;
    const py = this.panelY;
    const pw = this.panelWidth;
    const ph = this.panelHeight;
    const pad = 8 * s;
    const listW = Math.floor(160 * s);
    const hotbarH = Math.floor(50 * s);

    this.listRect = { x: px + pad, y: py + pad, w: listW, h: ph - hotbarH - pad * 3 };
    this.detailRect = { x: px + pad + listW + pad, y: py + pad, w: pw - listW - pad * 3, h: ph - hotbarH - pad * 3 };
    this.hotbarRect = { x: px + pad, y: py + ph - hotbarH - pad, w: pw - pad * 2, h: hotbarH };
  }

  // ========================
  // Skill List (Left Panel)
  // ========================

  private renderSkillList(ctx: CanvasRenderingContext2D): void {
    const r = this.listRect;
    const s = this.scale;
    const skills = this.player.customSkillsV2;

    // Background
    ctx.fillStyle = LIST_BG;
    roundRect(ctx, r.x, r.y, r.w, r.h, 6);
    ctx.fill();

    // Title
    ctx.fillStyle = GOLD;
    ctx.font = `bold ${Math.floor(13 * s)}px 'Segoe UI', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('Beceriler', r.x + r.w / 2, r.y + 6 * s);

    // [+ Yeni] button
    const btnH = Math.floor(26 * s);
    const btnY = r.y + 24 * s;
    const hoverNew = this.isInside(this.mouseX, this.mouseY, r.x + 4 * s, btnY, r.w - 8 * s, btnH);
    ctx.fillStyle = hoverNew ? '#3a3220' : BTN_BG;
    roundRect(ctx, r.x + 4 * s, btnY, r.w - 8 * s, btnH, 4);
    ctx.fill();
    ctx.strokeStyle = GOLD;
    ctx.lineWidth = 1;
    roundRect(ctx, r.x + 4 * s, btnY, r.w - 8 * s, btnH, 4);
    ctx.stroke();
    ctx.fillStyle = GOLD;
    ctx.font = `bold ${Math.floor(12 * s)}px 'Segoe UI', sans-serif`;
    ctx.fillText('+ Yeni', r.x + r.w / 2, btnY + 6 * s);

    // Skill entries
    const entryH = Math.floor(36 * s);
    const startY = btnY + btnH + 6 * s;
    ctx.save();
    ctx.beginPath();
    ctx.rect(r.x, startY, r.w, r.y + r.h - startY);
    ctx.clip();

    for (let i = 0; i < skills.length; i++) {
      const ey = startY + i * entryH - this.listScrollY;
      if (ey + entryH < startY || ey > r.y + r.h) continue;

      const skill = skills[i];
      const selected = i === this.selectedIndex;
      const hovered = this.isInside(this.mouseX, this.mouseY, r.x, ey, r.w, entryH);

      // Entry bg
      if (selected) {
        ctx.fillStyle = '#3a3020';
      } else if (hovered) {
        ctx.fillStyle = HOVER_BG;
      } else {
        ctx.fillStyle = 'transparent';
      }
      if (selected || hovered) {
        roundRect(ctx, r.x + 2, ey, r.w - 4, entryH - 2, 4);
        ctx.fill();
      }

      // Selected indicator
      if (selected) {
        ctx.fillStyle = GOLD;
        ctx.fillRect(r.x + 2, ey + 4, 3, entryH - 10);
      }

      // Delivery icon + name
      const info = DELIVERY_TYPE_INFO[skill.deliveryType];
      const elemInfo = ELEMENT_INFO[skill.element];
      ctx.font = `${Math.floor(11 * s)}px 'Segoe UI', sans-serif`;
      ctx.textAlign = 'left';
      ctx.fillStyle = selected ? GOLD : TEXT;
      ctx.fillText(`${info.icon} ${skill.name}`, r.x + 10 * s, ey + 6 * s);
      ctx.font = `${Math.floor(9 * s)}px 'Segoe UI', sans-serif`;
      ctx.fillStyle = elemInfo.color;
      ctx.fillText(`${elemInfo.icon} ${info.name}`, r.x + 10 * s, ey + 20 * s);
    }
    ctx.restore();
  }

  // ========================
  // Detail Panel (Right Side)
  // ========================

  private renderDetailPanel(ctx: CanvasRenderingContext2D): void {
    const r = this.detailRect;
    const s = this.scale;
    const skills = this.player.customSkillsV2;
    if (skills.length === 0) {
      ctx.fillStyle = TEXT_DIM;
      ctx.font = `${Math.floor(14 * s)}px 'Segoe UI', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Beceri yok. "Yeni" butonuna tikla.', r.x + r.w / 2, r.y + r.h / 2);
      return;
    }

    const skill = skills[this.selectedIndex];
    if (!skill) return;

    const info = DELIVERY_TYPE_INFO[skill.deliveryType];
    const elemInfo = ELEMENT_INFO[skill.element];

    // Background
    ctx.fillStyle = BG;
    roundRect(ctx, r.x, r.y, r.w, r.h, 6);
    ctx.fill();

    // Header
    const hdrH = 44 * s;
    ctx.fillStyle = '#1e1a15';
    roundRect(ctx, r.x, r.y, r.w, hdrH, 6);
    ctx.fill();

    // Skill name
    ctx.fillStyle = GOLD;
    ctx.font = `bold ${Math.floor(15 * s)}px 'Segoe UI', sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillText(skill.name, r.x + 10 * s, r.y + 6 * s);

    // Element badge
    ctx.fillStyle = elemInfo.color;
    ctx.font = `${Math.floor(11 * s)}px 'Segoe UI', sans-serif`;
    ctx.fillText(`${elemInfo.icon} ${elemInfo.name}`, r.x + 10 * s, r.y + 24 * s);

    // Type badge
    ctx.fillStyle = info.color;
    const typeText = `${info.icon} ${info.name}`;
    ctx.fillText(typeText, r.x + 100 * s, r.y + 24 * s);

    // Mana / Cooldown
    ctx.textAlign = 'right';
    ctx.fillStyle = '#5dade2';
    const costText = this.getCostText(skill);
    ctx.fillText(costText, r.x + r.w - 10 * s, r.y + 6 * s);
    ctx.fillStyle = TEXT_DIM;
    ctx.fillText(`CD: ${skill.cooldown.toFixed(1)}s`, r.x + r.w - 10 * s, r.y + 24 * s);

    // Tab buttons
    const tabY = r.y + hdrH + 4 * s;
    const tabW = 90 * s;
    const tabH = 24 * s;
    for (const tab of ['settings', 'stats'] as EditorTab[]) {
      const tx = tab === 'settings' ? r.x + 8 * s : r.x + 8 * s + tabW + 4 * s;
      const active = this.activeTab === tab;
      const hovered = this.isInside(this.mouseX, this.mouseY, tx, tabY, tabW, tabH);
      ctx.fillStyle = active ? '#3a3020' : (hovered ? HOVER_BG : BTN_BG);
      roundRect(ctx, tx, tabY, tabW, tabH, 4);
      ctx.fill();
      if (active) {
        ctx.strokeStyle = GOLD;
        ctx.lineWidth = 1;
        roundRect(ctx, tx, tabY, tabW, tabH, 4);
        ctx.stroke();
      }
      ctx.fillStyle = active ? GOLD : TEXT_DIM;
      ctx.font = `${Math.floor(11 * s)}px 'Segoe UI', sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(tab === 'settings' ? 'Ayarlar' : 'Istatistik', tx + tabW / 2, tabY + 6 * s);
    }

    // Content area
    const contentY = tabY + tabH + 8 * s;
    const contentH = r.y + r.h - contentY - 4 * s;

    if (this.activeTab === 'settings') {
      const choicesH = this.renderChoices(ctx, r.x + 8 * s, contentY, r.w - 16 * s, skill);
      this.renderSliders(ctx, r.x + 8 * s, contentY + choicesH, r.w - 16 * s, contentH - choicesH, skill);
    } else {
      this.renderStats(ctx, r.x + 8 * s, contentY, r.w - 16 * s, contentH, skill);
    }
  }

  // ========================
  // Sliders
  // ========================

  private renderSliders(
    ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, skill: CustomSkillV2
  ): void {
    const s = this.scale;
    const configs = DELIVERY_TYPE_SLIDERS[skill.deliveryType];
    const elemColor = ELEMENT_INFO[skill.element].color;
    const sliders = skill.sliders as Record<string, number>;
    const sliderH = Math.floor(32 * s);
    let cy = y;

    for (const cfg of configs) {
      if (cy + sliderH > y + h) break;
      const val = sliders[cfg.key];
      if (val === undefined) continue; // skip non-numeric (e.g. shape)

      const norm = (val - cfg.min) / (cfg.max - cfg.min);
      const trackX = x + 120 * s;
      const trackW = w - 190 * s;
      const trackY = cy + 14 * s;
      const trackH = 6 * s;

      // Hover detection
      const hovered = this.isInside(this.mouseX, this.mouseY, trackX - 4, cy, trackW + 8, sliderH);
      // Label
      ctx.fillStyle = hovered ? TEXT : TEXT_DIM;
      ctx.font = `${Math.floor(11 * s)}px 'Segoe UI', sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(cfg.label, x, cy + sliderH / 2);

      // Track bg
      ctx.fillStyle = SLIDER_TRACK;
      roundRect(ctx, trackX, trackY, trackW, trackH, 3);
      ctx.fill();

      // Filled portion
      const fillW = trackW * norm;
      if (fillW > 0) {
        ctx.fillStyle = this.draggingSlider === cfg.key ? this.lightenColor(elemColor, 0.3) : elemColor;
        roundRect(ctx, trackX, trackY, fillW, trackH, 3);
        ctx.fill();
      }

      // Thumb
      const thumbX = trackX + fillW;
      ctx.beginPath();
      ctx.arc(thumbX, trackY + trackH / 2, 5 * s, 0, Math.PI * 2);
      ctx.fillStyle = hovered || this.draggingSlider === cfg.key ? '#fff' : TEXT;
      ctx.fill();

      // Value text
      ctx.fillStyle = TEXT;
      ctx.textAlign = 'right';
      ctx.font = `bold ${Math.floor(11 * s)}px 'Segoe UI', sans-serif`;
      const valStr = Number.isInteger(cfg.step) ? `${val}` : `${val.toFixed(1)}`;
      ctx.fillText(`${valStr} ${cfg.unit || ''}`, x + w, cy + sliderH / 2);

      cy += sliderH;
    }
  }

  // ========================
  // Stats Tab
  // ========================

  private renderStats(
    ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, skill: CustomSkillV2
  ): void {
    const s = this.scale;
    const info = DELIVERY_TYPE_INFO[skill.deliveryType];
    const elemInfo = ELEMENT_INFO[skill.element];
    const lineH = 22 * s;
    let cy = y;

    const lines: [string, string][] = [
      ['Teslimat Turu', `${info.icon} ${info.name}`],
      ['Element', `${elemInfo.icon} ${elemInfo.name}`],
      ['Seviye', `${skill.level}`],
      ['Mana Maliyeti', `${skill.manaCost}`],
      ['Bekleme Suresi', `${skill.cooldown.toFixed(1)}s`],
    ];

    if (skill.manaDrainPerSecond) lines.push(['Mana Tuketimi', `${skill.manaDrainPerSecond} /s`]);
    if (skill.manaReservePercent) lines.push(['Mana Rezerv', `${skill.manaReservePercent}%`]);
    if (skill.manaReserveFlat !== undefined) lines.push(['Mana Rezerv', `${skill.manaReserveFlat} mana`]);
    lines.push(['Soket', `${skill.sockets.filter(s => s !== null).length}/${skill.socketCount}`]);

    // Slider values
    const sliders = skill.sliders as Record<string, number>;
    const configs = DELIVERY_TYPE_SLIDERS[skill.deliveryType];
    for (const cfg of configs) {
      const val = sliders[cfg.key];
      if (val === undefined) continue;
      const valStr = Number.isInteger(cfg.step) ? `${val}` : `${val.toFixed(1)}`;
      lines.push([cfg.label, `${valStr} ${cfg.unit || ''}`]);
    }

    for (const [label, value] of lines) {
      if (cy + lineH > y + h) break;
      ctx.fillStyle = TEXT_DIM;
      ctx.font = `${Math.floor(11 * s)}px 'Segoe UI', sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(label, x, cy);
      ctx.fillStyle = TEXT;
      ctx.textAlign = 'right';
      ctx.fillText(value, x + w, cy);
      cy += lineH;
    }
  }

  // ========================
  // Hotbar
  // ========================

  private renderHotbar(ctx: CanvasRenderingContext2D): void {
    const r = this.hotbarRect;
    const s = this.scale;
    const slotSize = Math.floor(38 * s);
    const gap = 6 * s;
    const totalW = 6 * slotSize + 5 * gap;
    const startX = r.x + (r.w - totalW - 80 * s) / 2;

    // Background
    ctx.fillStyle = '#14110e';
    roundRect(ctx, r.x, r.y, r.w, r.h, 6);
    ctx.fill();

    // Label
    ctx.fillStyle = TEXT_DIM;
    ctx.font = `${Math.floor(10 * s)}px 'Segoe UI', sans-serif`;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText('Hotbar:', r.x + 6 * s, r.y + r.h / 2);

    for (let i = 0; i < 6; i++) {
      const sx = startX + i * (slotSize + gap);
      const sy = r.y + (r.h - slotSize) / 2;
      const hovered = this.isInside(this.mouseX, this.mouseY, sx, sy, slotSize, slotSize);
      const skill = this.player.skillHotbar.slots[i];

      // Slot bg
      ctx.fillStyle = hovered ? '#3a3020' : BTN_BG;
      roundRect(ctx, sx, sy, slotSize, slotSize, 4);
      ctx.fill();
      ctx.strokeStyle = skill ? GOLD : BORDER;
      ctx.lineWidth = 1;
      roundRect(ctx, sx, sy, slotSize, slotSize, 4);
      ctx.stroke();

      // Slot number
      ctx.fillStyle = TEXT_DIM;
      ctx.font = `${Math.floor(9 * s)}px 'Segoe UI', sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(`${i + 1}`, sx + 3, sy + 2);

      // Skill icon if assigned
      if (skill && 'version' in skill && skill.version === 2) {
        const v2 = skill as CustomSkillV2;
        const dInfo = DELIVERY_TYPE_INFO[v2.deliveryType];
        ctx.font = `${Math.floor(16 * s)}px 'Segoe UI Emoji', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = ELEMENT_INFO[v2.element].color;
        ctx.fillText(dInfo.icon, sx + slotSize / 2, sy + slotSize / 2 + 2);
      } else if (skill) {
        // V1 skill fallback
        ctx.font = `${Math.floor(10 * s)}px 'Segoe UI', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = TEXT_DIM;
        ctx.fillText('V1', sx + slotSize / 2, sy + slotSize / 2);
      }
    }

    // Delete button
    const delW = 60 * s;
    const delH = 28 * s;
    const delX = r.x + r.w - delW - 8 * s;
    const delY = r.y + (r.h - delH) / 2;
    const hoverDel = this.isInside(this.mouseX, this.mouseY, delX, delY, delW, delH);
    ctx.fillStyle = hoverDel ? '#5a2020' : '#3a1818';
    roundRect(ctx, delX, delY, delW, delH, 4);
    ctx.fill();
    ctx.strokeStyle = '#c0392b';
    ctx.lineWidth = 1;
    roundRect(ctx, delX, delY, delW, delH, 4);
    ctx.stroke();
    ctx.fillStyle = '#e74c3c';
    ctx.font = `bold ${Math.floor(11 * s)}px 'Segoe UI', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Sil', delX + delW / 2, delY + delH / 2);
  }

  // ========================
  // New Skill Creation Flow
  // ========================

  private renderCreationFlow(ctx: CanvasRenderingContext2D): void {
    const s = this.scale;
    const px = this.panelX;
    const py = this.panelY;
    const pw = this.panelWidth;
    const ph = this.panelHeight;

    if (this.creationStep === 'type') {
      this.renderTypeSelection(ctx, px, py, pw, ph, s);
    } else {
      this.renderElementSelection(ctx, px, py, pw, ph, s);
    }

    // Back / Cancel button
    const btnW = 80 * s;
    const btnH = 28 * s;
    const btnX = px + pw / 2 - btnW / 2;
    const btnY = py + ph - btnH - 12 * s;
    const hoverBack = this.isInside(this.mouseX, this.mouseY, btnX, btnY, btnW, btnH);
    ctx.fillStyle = hoverBack ? '#3a2020' : BTN_BG;
    roundRect(ctx, btnX, btnY, btnW, btnH, 4);
    ctx.fill();
    ctx.strokeStyle = BORDER;
    ctx.lineWidth = 1;
    roundRect(ctx, btnX, btnY, btnW, btnH, 4);
    ctx.stroke();
    ctx.fillStyle = TEXT;
    ctx.font = `${Math.floor(11 * s)}px 'Segoe UI', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.creationStep === 'type' ? 'Iptal' : 'Geri', btnX + btnW / 2, btnY + btnH / 2);
  }

  private renderTypeSelection(
    ctx: CanvasRenderingContext2D, px: number, py: number, pw: number, ph: number, s: number
  ): void {
    // Title
    ctx.fillStyle = GOLD;
    ctx.font = `bold ${Math.floor(16 * s)}px 'Segoe UI', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('Teslimat Turu Sec', px + pw / 2, py + 14 * s);

    // 2x5 grid
    const cols = 5;
    const rows = 2;
    const cardW = Math.floor((pw - 60 * s) / cols);
    const cardH = Math.floor(Math.min(80 * s, (ph - 100 * s) / rows));
    const gridW = cols * cardW;
    const gridStartX = px + (pw - gridW) / 2;
    const gridStartY = py + 44 * s;

    for (let i = 0; i < DELIVERY_TYPES.length; i++) {
      const dt = DELIVERY_TYPES[i];
      const info = DELIVERY_TYPE_INFO[dt];
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cx = gridStartX + col * cardW;
      const cy = gridStartY + row * (cardH + 8 * s);

      const hovered = this.isInside(this.mouseX, this.mouseY, cx, cy, cardW - 4, cardH);
      ctx.fillStyle = hovered ? '#2a2520' : BTN_BG;
      roundRect(ctx, cx, cy, cardW - 4, cardH, 6);
      ctx.fill();
      ctx.strokeStyle = hovered ? info.color : BORDER;
      ctx.lineWidth = hovered ? 2 : 1;
      roundRect(ctx, cx, cy, cardW - 4, cardH, 6);
      ctx.stroke();

      // Icon
      ctx.font = `${Math.floor(20 * s)}px 'Segoe UI Emoji', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = info.color;
      ctx.fillText(info.icon, cx + (cardW - 4) / 2, cy + 8 * s);

      // Name
      ctx.font = `bold ${Math.floor(10 * s)}px 'Segoe UI', sans-serif`;
      ctx.fillStyle = hovered ? TEXT : TEXT_DIM;
      ctx.fillText(info.name, cx + (cardW - 4) / 2, cy + 34 * s);

      // Short description
      ctx.font = `${Math.floor(8 * s)}px 'Segoe UI', sans-serif`;
      ctx.fillStyle = TEXT_DIM;
      const desc = info.description.length > 30 ? info.description.slice(0, 28) + '..' : info.description;
      ctx.fillText(desc, cx + (cardW - 4) / 2, cy + 50 * s);
    }
  }

  private renderElementSelection(
    ctx: CanvasRenderingContext2D, px: number, py: number, pw: number, _ph: number, s: number
  ): void {
    // Title
    ctx.fillStyle = GOLD;
    ctx.font = `bold ${Math.floor(16 * s)}px 'Segoe UI', sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';
    ctx.fillText('Element Sec', px + pw / 2, py + 14 * s);

    // Subtitle: selected type
    if (this.pendingDeliveryType) {
      const dtInfo = DELIVERY_TYPE_INFO[this.pendingDeliveryType];
      ctx.fillStyle = dtInfo.color;
      ctx.font = `${Math.floor(12 * s)}px 'Segoe UI', sans-serif`;
      ctx.fillText(`${dtInfo.icon} ${dtInfo.name}`, px + pw / 2, py + 36 * s);
    }

    // 5 element buttons
    const btnW = Math.floor(Math.min(120 * s, (pw - 60 * s) / 5));
    const btnH = Math.floor(80 * s);
    const totalW = 5 * btnW + 4 * 8 * s;
    const startX = px + (pw - totalW) / 2;
    const startY = py + 70 * s;

    for (let i = 0; i < ELEMENTS.length; i++) {
      const elem = ELEMENTS[i];
      const eInfo = ELEMENT_INFO[elem];
      const bx = startX + i * (btnW + 8 * s);
      const hovered = this.isInside(this.mouseX, this.mouseY, bx, startY, btnW, btnH);

      ctx.fillStyle = hovered ? '#2a2520' : BTN_BG;
      roundRect(ctx, bx, startY, btnW, btnH, 6);
      ctx.fill();
      ctx.strokeStyle = hovered ? eInfo.color : BORDER;
      ctx.lineWidth = hovered ? 2 : 1;
      roundRect(ctx, bx, startY, btnW, btnH, 6);
      ctx.stroke();

      // Icon
      ctx.font = `${Math.floor(24 * s)}px 'Segoe UI Emoji', sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'top';
      ctx.fillStyle = eInfo.color;
      ctx.fillText(eInfo.icon, bx + btnW / 2, startY + 10 * s);

      // Name
      ctx.font = `bold ${Math.floor(11 * s)}px 'Segoe UI', sans-serif`;
      ctx.fillStyle = hovered ? TEXT : TEXT_DIM;
      ctx.fillText(eInfo.name, bx + btnW / 2, startY + 50 * s);
    }
  }

  // ========================
  // Input Handling
  // ========================

  handleInput(input: InputManager): boolean {
    if (!this.isOpen) return false;
    if (input.isKeyPressed('Escape')) {
      if (this.isCreating) {
        if (this.creationStep === 'element') {
          this.creationStep = 'type';
        } else {
          this.isCreating = false;
        }
      } else {
        this.close();
      }
      return true;
    }
    return false;
  }

  handleMouseDown(x: number, y: number): void {
    if (!this.isOpen) return;
    this.mouseX = x;
    this.mouseY = y;

    if (this.isCreating) {
      this.handleCreationClick(x, y);
      return;
    }

    // Check choice buttons + slider drag in settings tab
    const skills = this.player.customSkillsV2;
    if (skills.length > 0 && this.activeTab === 'settings') {
      const skill = skills[this.selectedIndex];
      if (skill) {
        // Choice buttons (above sliders)
        const s = this.scale;
        const editorX = this.listRect.x + this.listRect.w + 8 * s;
        const r = { x: editorX, y: this.listRect.y, w: this.panelWidth - this.listRect.w - 24 * s, h: this.listRect.h };
        const tabH = Math.floor(28 * s);
        const tabY = r.y + 70 * s;
        const choicesY = tabY + tabH + 8 * s;
        if (this.handleChoiceClick(x, y, skill, choicesY)) return;

        // Slider drag
        const sliderHit = this.hitTestSlider(x, y, skill);
        if (sliderHit) {
          this.draggingSlider = sliderHit;
          this.updateSliderFromMouse(x, skill);
          return;
        }
      }
    }

    // Check tab buttons
    this.handleTabClick(x, y);

    // Check skill list click
    this.handleListClick(x, y);

    // Check hotbar click
    this.handleHotbarClick(x, y);

    // Check delete button
    this.handleDeleteClick(x, y);

    // Check new button
    this.handleNewButtonClick(x, y);
  }

  handleMouseMove(x: number, y: number): void {
    this.mouseX = x;
    this.mouseY = y;
    if (this.draggingSlider && this.player.customSkillsV2.length > 0) {
      const skill = this.player.customSkillsV2[this.selectedIndex];
      if (skill) this.updateSliderFromMouse(x, skill);
    }
  }

  handleMouseUp(_x: number, _y: number): void {
    this.draggingSlider = null;
  }

  handleWheel(deltaY: number): void {
    if (!this.isOpen) return;
    // Scroll the skill list if mouse is over it
    if (this.isInside(this.mouseX, this.mouseY, this.listRect.x, this.listRect.y, this.listRect.w, this.listRect.h)) {
      const maxScroll = Math.max(0, this.player.customSkillsV2.length * 36 * this.scale - this.listRect.h + 60 * this.scale);
      this.listScrollY = Math.max(0, Math.min(maxScroll, this.listScrollY + deltaY * 0.5));
    }
  }

  // ========================
  // Click Handlers
  // ========================

  private handleCreationClick(x: number, y: number): void {
    const s = this.scale;
    const px = this.panelX;
    const py = this.panelY;
    const pw = this.panelWidth;
    const ph = this.panelHeight;

    // Back / Cancel
    const btnW = 80 * s;
    const btnH = 28 * s;
    const btnX = px + pw / 2 - btnW / 2;
    const btnY = py + ph - btnH - 12 * s;
    if (this.isInside(x, y, btnX, btnY, btnW, btnH)) {
      if (this.creationStep === 'element') {
        this.creationStep = 'type';
      } else {
        this.isCreating = false;
      }
      return;
    }

    if (this.creationStep === 'type') {
      // Hit test delivery type grid
      const cols = 5;
      const cardW = Math.floor((pw - 60 * s) / cols);
      const cardH = Math.floor(Math.min(80 * s, (ph - 100 * s) / 2));
      const gridW = cols * cardW;
      const gridStartX = px + (pw - gridW) / 2;
      const gridStartY = py + 44 * s;

      for (let i = 0; i < DELIVERY_TYPES.length; i++) {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const cx = gridStartX + col * cardW;
        const cy = gridStartY + row * (cardH + 8 * s);
        if (this.isInside(x, y, cx, cy, cardW - 4, cardH)) {
          this.pendingDeliveryType = DELIVERY_TYPES[i];
          this.creationStep = 'element';
          return;
        }
      }
    } else if (this.creationStep === 'element' && this.pendingDeliveryType) {
      // Hit test element buttons
      const btnEW = Math.floor(Math.min(120 * s, (pw - 60 * s) / 5));
      const btnEH = Math.floor(80 * s);
      const totalW = 5 * btnEW + 4 * 8 * s;
      const startX = px + (pw - totalW) / 2;
      const startY = py + 70 * s;

      for (let i = 0; i < ELEMENTS.length; i++) {
        const bx = startX + i * (btnEW + 8 * s);
        if (this.isInside(x, y, bx, startY, btnEW, btnEH)) {
          this.createNewSkill(this.pendingDeliveryType, ELEMENTS[i]);
          return;
        }
      }
    }
  }

  private handleTabClick(x: number, y: number): void {
    const r = this.detailRect;
    const s = this.scale;
    const hdrH = 44 * s;
    const tabY = r.y + hdrH + 4 * s;
    const tabW = 90 * s;
    const tabH = 24 * s;

    if (this.isInside(x, y, r.x + 8 * s, tabY, tabW, tabH)) {
      this.activeTab = 'settings';
    } else if (this.isInside(x, y, r.x + 8 * s + tabW + 4 * s, tabY, tabW, tabH)) {
      this.activeTab = 'stats';
    }
  }

  private handleListClick(x: number, y: number): void {
    const r = this.listRect;
    const s = this.scale;
    const skills = this.player.customSkillsV2;
    const btnH = Math.floor(26 * s);
    const startY = r.y + 24 * s + btnH + 6 * s;
    const entryH = Math.floor(36 * s);

    for (let i = 0; i < skills.length; i++) {
      const ey = startY + i * entryH - this.listScrollY;
      if (this.isInside(x, y, r.x, ey, r.w, entryH)) {
        this.selectedIndex = i;
        return;
      }
    }
  }

  private handleNewButtonClick(x: number, y: number): void {
    const r = this.listRect;
    const s = this.scale;
    const btnH = Math.floor(26 * s);
    const btnY = r.y + 24 * s;
    if (this.isInside(x, y, r.x + 4 * s, btnY, r.w - 8 * s, btnH)) {
      this.isCreating = true;
      this.creationStep = 'type';
      this.pendingDeliveryType = null;
    }
  }

  private handleHotbarClick(x: number, y: number): void {
    const r = this.hotbarRect;
    const s = this.scale;
    const slotSize = Math.floor(38 * s);
    const gap = 6 * s;
    const totalW = 6 * slotSize + 5 * gap;
    const startX = r.x + (r.w - totalW - 80 * s) / 2;
    const skills = this.player.customSkillsV2;

    for (let i = 0; i < 6; i++) {
      const sx = startX + i * (slotSize + gap);
      const sy = r.y + (r.h - slotSize) / 2;
      if (this.isInside(x, y, sx, sy, slotSize, slotSize)) {
        if (skills.length > 0 && this.selectedIndex < skills.length) {
          const skill = skills[this.selectedIndex];
          this.player.skillHotbar.assign(skill, i);
          this.callbacks.onSkillChange?.();
        }
        return;
      }
    }
  }

  private handleDeleteClick(x: number, y: number): void {
    const r = this.hotbarRect;
    const s = this.scale;
    const delW = 60 * s;
    const delH = 28 * s;
    const delX = r.x + r.w - delW - 8 * s;
    const delY = r.y + (r.h - delH) / 2;

    if (this.isInside(x, y, delX, delY, delW, delH)) {
      this.deleteSelectedSkill();
    }
  }

  // ========================
  // Slider Interaction
  // ========================

  private hitTestSlider(x: number, y: number, skill: CustomSkillV2): string | null {
    const r = this.detailRect;
    const s = this.scale;
    const hdrH = 44 * s;
    const tabH = 24 * s;
    const contentY = r.y + hdrH + 4 * s + tabH + 8 * s;
    const configs = DELIVERY_TYPE_SLIDERS[skill.deliveryType];
    const sliderH = Math.floor(32 * s);
    const trackX = r.x + 8 * s + 120 * s;
    const trackW = (r.w - 16 * s) - 190 * s;

    for (let i = 0; i < configs.length; i++) {
      const cfg = configs[i];
      const val = (skill.sliders as Record<string, number>)[cfg.key];
      if (val === undefined) continue;
      const cy = contentY + i * sliderH;
      if (this.isInside(x, y, trackX - 8, cy, trackW + 16, sliderH)) {
        return cfg.key;
      }
    }
    return null;
  }

  private updateSliderFromMouse(mouseX: number, skill: CustomSkillV2): void {
    if (!this.draggingSlider) return;
    const r = this.detailRect;
    const s = this.scale;
    const trackX = r.x + 8 * s + 120 * s;
    const trackW = (r.w - 16 * s) - 190 * s;

    const configs = DELIVERY_TYPE_SLIDERS[skill.deliveryType];
    const cfg = configs.find(c => c.key === this.draggingSlider);
    if (!cfg) return;

    const norm = Math.max(0, Math.min(1, (mouseX - trackX) / trackW));
    const rawVal = cfg.min + norm * (cfg.max - cfg.min);
    const snapped = Math.round(rawVal / cfg.step) * cfg.step;
    const clamped = Math.max(cfg.min, Math.min(cfg.max, snapped));

    SkillCreatorV2.adjustSlider(skill, this.draggingSlider, clamped);
    this.callbacks.onSkillChange?.();
  }

  // ========================
  // Actions
  // ========================

  private createNewSkill(deliveryType: SkillDeliveryType, element: ElementType): void {
    const dtInfo = DELIVERY_TYPE_INFO[deliveryType];
    const eInfo = ELEMENT_INFO[element];
    const idx = this.player.customSkillsV2.length + 1;
    const name = `${eInfo.name} ${dtInfo.name} ${idx}`;

    const skill = SkillCreatorV2.createSkill(deliveryType, element, name);
    this.player.customSkillsV2.push(skill);
    this.selectedIndex = this.player.customSkillsV2.length - 1;
    this.isCreating = false;
    this.creationStep = 'type';
    this.pendingDeliveryType = null;
    this.callbacks.onSkillChange?.();
  }

  private deleteSelectedSkill(): void {
    const skills = this.player.customSkillsV2;
    if (skills.length <= 1) return;

    const skill = skills[this.selectedIndex];
    if (!skill) return;

    // Remove from hotbar
    for (let i = 0; i < 6; i++) {
      if (this.player.skillHotbar.slots[i]?.id === skill.id) {
        this.player.skillHotbar.clear(i);
      }
    }

    skills.splice(this.selectedIndex, 1);
    if (this.selectedIndex >= skills.length) {
      this.selectedIndex = skills.length - 1;
    }
    this.callbacks.onSkillChange?.();
  }

  // ========================
  // Helpers
  // ========================

  private getCostText(skill: CustomSkillV2): string {
    if (skill.manaDrainPerSecond) return `${skill.manaDrainPerSecond} mana/s`;
    if (skill.manaReservePercent) return `Rezerv: ${skill.manaReservePercent}%`;
    if (skill.manaReserveFlat !== undefined) return `Rezerv: ${skill.manaReserveFlat} mana`;
    return `Mana: ${skill.manaCost}`;
  }

  private isInside(px: number, py: number, rx: number, ry: number, rw: number, rh: number): boolean {
    return px >= rx && px <= rx + rw && py >= ry && py <= ry + rh;
  }

  private lightenColor(hex: string, amount: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const lr = Math.min(255, Math.floor(r + (255 - r) * amount));
    const lg = Math.min(255, Math.floor(g + (255 - g) * amount));
    const lb = Math.min(255, Math.floor(b + (255 - b) * amount));
    return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`;
  }

  // ========================
  // Type-specific choices (non-slider selections)
  // ========================

  /** Get which choice groups apply to a delivery type */
  private getChoiceGroups(dt: SkillDeliveryType): { key: string; label: string; options: Record<string, { name: string; icon: string; desc: string }> }[] {
    const groups: { key: string; label: string; options: Record<string, { name: string; icon: string; desc: string }> }[] = [];
    switch (dt) {
      case 'melee':
        groups.push({ key: 'meleeType', label: 'Vuruş Tipi', options: SKILL_CHOICES.meleeType });
        break;
      case 'projectile':
        groups.push({ key: 'projectileContact', label: 'Temas', options: SKILL_CHOICES.projectileContact });
        break;
      case 'aoe':
        groups.push({ key: 'shape', label: 'Şekil', options: SKILL_CHOICES.meleeType }); // placeholder - use AOE shapes
        // Actually use proper AOE choices:
        groups.length = 0;
        groups.push(
          { key: 'aoeShape', label: 'Şekil', options: { circle: { name: 'Daire', icon: '⭕', desc: 'Dairesel' }, cone: { name: 'Koni', icon: '🔺', desc: 'Yelpaze' }, line: { name: 'Çizgi', icon: '➖', desc: 'Duvar' }, leap: { name: 'Sıçrama', icon: '⬆', desc: 'Atla+patla' } } },
          { key: 'aoeTrigger', label: 'Tetikleme', options: SKILL_CHOICES.aoeTrigger },
          { key: 'aoePosition', label: 'Konum', options: SKILL_CHOICES.aoePosition },
        );
        break;
      case 'beam':
        groups.push({ key: 'beamMode', label: 'Mod', options: SKILL_CHOICES.beamMode });
        break;
      case 'buff':
        groups.push({ key: 'buffType', label: 'Buff Tipi', options: SKILL_CHOICES.buffType });
        break;
      case 'trap':
        groups.push({ key: 'trapTrigger', label: 'Tetikleme', options: SKILL_CHOICES.trapTrigger });
        break;
      case 'totem':
        groups.push({ key: 'totemType', label: 'Totem Tipi', options: SKILL_CHOICES.totemType });
        break;
      case 'curse':
        groups.push(
          { key: 'curseType', label: 'Lanet Tipi', options: SKILL_CHOICES.curseType },
          { key: 'curseTarget', label: 'Hedef', options: SKILL_CHOICES.curseTarget },
        );
        break;
      case 'minion':
        groups.push({ key: 'minionType', label: 'Minyon Tipi', options: SKILL_CHOICES.minionType });
        break;
    }
    return groups;
  }

  /** Render choice buttons above sliders. Returns total height consumed. */
  private renderChoices(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, skill: CustomSkillV2): number {
    const s = this.scale;
    const groups = this.getChoiceGroups(skill.deliveryType);
    if (groups.length === 0) return 0;

    let cy = y;
    const btnH = Math.floor(26 * s);
    const btnGap = Math.floor(4 * s);
    const groupGap = Math.floor(8 * s);
    const labelH = Math.floor(16 * s);

    for (const group of groups) {
      // Group label
      ctx.fillStyle = GOLD;
      ctx.font = `bold ${Math.floor(10 * s)}px 'Segoe UI', sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(group.label, x, cy);
      cy += labelH;

      // Option buttons
      const entries = Object.entries(group.options);
      const totalGaps = (entries.length - 1) * btnGap;
      const btnW = Math.floor((w - totalGaps) / entries.length);
      const currentVal = (skill as any)[group.key] || entries[0][0];

      for (let i = 0; i < entries.length; i++) {
        const [optKey, opt] = entries[i];
        const bx = x + i * (btnW + btnGap);
        const isActive = currentVal === optKey;

        // Button background
        ctx.fillStyle = isActive ? '#2a2520' : '#151310';
        roundRect(ctx, bx, cy, btnW, btnH, 4);
        ctx.fill();

        // Active border
        if (isActive) {
          ctx.strokeStyle = GOLD;
          ctx.lineWidth = 1.5;
          roundRect(ctx, bx, cy, btnW, btnH, 4);
          ctx.stroke();
        } else {
          ctx.strokeStyle = BORDER;
          ctx.lineWidth = 0.5;
          roundRect(ctx, bx, cy, btnW, btnH, 4);
          ctx.stroke();
        }

        // Icon + text
        ctx.fillStyle = isActive ? TEXT : TEXT_DIM;
        ctx.font = `${Math.floor(10 * s)}px 'Segoe UI', sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(`${opt.icon} ${opt.name}`, bx + btnW / 2, cy + btnH / 2);
      }

      cy += btnH + groupGap;
    }

    return cy - y;
  }

  /** Handle click on choice buttons */
  private handleChoiceClick(mx: number, my: number, skill: CustomSkillV2, choicesY: number): boolean {
    const s = this.scale;
    const groups = this.getChoiceGroups(skill.deliveryType);
    if (groups.length === 0) return false;

    const editorX = this.listRect.x + this.listRect.w + 8 * s;
    const r = { x: editorX, y: this.listRect.y, w: this.panelWidth - this.listRect.w - 24 * s, h: this.listRect.h };
    const x = r.x + 8 * s;
    const w = r.w - 16 * s;

    let cy = choicesY;
    const btnH = Math.floor(26 * s);
    const btnGap = Math.floor(4 * s);
    const groupGap = Math.floor(8 * s);
    const labelH = Math.floor(16 * s);

    for (const group of groups) {
      cy += labelH;
      const entries = Object.entries(group.options);
      const totalGaps = (entries.length - 1) * btnGap;
      const btnW = Math.floor((w - totalGaps) / entries.length);

      for (let i = 0; i < entries.length; i++) {
        const [optKey] = entries[i];
        const bx = x + i * (btnW + btnGap);

        if (mx >= bx && mx <= bx + btnW && my >= cy && my <= cy + btnH) {
          (skill as any)[group.key] = optKey;
          // For AOE shape, also update the slider
          if (group.key === 'aoeShape') {
            (skill.sliders as any).shape = optKey;
          }
          this.callbacks.onSkillChange?.();
          return true;
        }
      }

      cy += btnH + groupGap;
    }

    return false;
  }
}

// ========================
// Canvas helper: rounded rectangle path
// ========================

function roundRect(
  ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
